import { UIEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLazyGetStorageLocationsQuery } from '@/app/services/locations';
import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { SELECTION_LIMIT } from '@/constants';
import { cn } from '@/lib/utils';
import { storageLocationsOptions } from '@/services/lookups';
import { SortEnumType } from '@/types.gql';

export type StorageLocationOption = {
  id: string;
  name?: string | null;
  siteId?: string | null;
};

type StorageLocationComboboxProps = {
  value?: StorageLocationOption | null;
  onChange?: (location: StorageLocationOption | null) => void;
  placeholder?: string;
  className?: string;
  siteIds?: string[];
  pagingEnabled?: boolean;
  disabled?: boolean;
};

export const StorageLocationCombobox = ({
  value,
  onChange,
  placeholder,
  className,
  siteIds,
  pagingEnabled = false,
  disabled = false,
}: StorageLocationComboboxProps) => {
  const { t } = useTranslation();
  const [locationSearch, setLocationSearch] = useState('');
  const debouncedSearch = useDebounce(locationSearch);
  const [queryLocations] = useLazyGetStorageLocationsQuery();
  const [locations, setLocations] = useState<StorageLocationOption[]>([]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      ...storageLocationsOptions({
        search: debouncedSearch || undefined,
        siteIds,
      }),
      enabled: pagingEnabled,
    });

  const pagedLocations = useMemo(() => data?.pages.flat() ?? [], [data]);

  useEffect(() => {
    setLocationSearch(value?.name ?? '');
  }, [value]);

  const fetchLocations = useCallback(
    async (search: string) => {
      const where: Record<string, unknown> = {};
      if (search) {
        where.name = { contains: search };
      }
      if (siteIds && siteIds.length > 0) {
        where.siteId = { in: siteIds };
      }
      const { data } = await queryLocations({
        skip: 0,
        take: SELECTION_LIMIT,
        order: [{ name: SortEnumType.ASC }],
        where,
      });
      setLocations(data ?? []);
    },
    [queryLocations, siteIds],
  );

  useEffect(() => {
    if (!pagingEnabled) {
      fetchLocations(debouncedSearch);
    }
  }, [debouncedSearch, fetchLocations, pagingEnabled]);

  const handleListScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      if (!pagingEnabled || !hasNextPage || isFetchingNextPage) {
        return;
      }

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= 40) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, pagingEnabled],
  );

  const displayedLocations = pagingEnabled ? pagedLocations : locations;

  return (
    <Combobox
      items={displayedLocations}
      value={value ?? null}
      onValueChange={onChange ?? (() => {})}
      itemToStringValue={(loc) => loc.id}
      itemToStringLabel={(loc) => loc.name ?? ''}
    >
      <ComboboxInput
        placeholder={
          placeholder ??
          t('common.selectStorageLocation', 'Select storage location')
        }
        className={cn('w-full', className)}
        disabled={disabled}
        value={locationSearch}
        onChange={(e) => {
          setLocationSearch(e.target.value);
          onChange?.(null);
        }}
        showClear
      />
      <ComboboxContent className="min-w-(--anchor-width)">
        <ComboboxEmpty>
          {t('common.noResultsFound', 'No results found')}
        </ComboboxEmpty>
        <ComboboxList onScroll={pagingEnabled ? handleListScroll : undefined}>
          {(location: StorageLocationOption) => (
            <ComboboxItem key={location.id} value={location}>
              {location.name}
            </ComboboxItem>
          )}
        </ComboboxList>
        {pagingEnabled && isFetchingNextPage && (
          <div className="text-muted-foreground flex items-center justify-center gap-2 p-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {t('common.loading')}
          </div>
        )}
      </ComboboxContent>
    </Combobox>
  );
};
